import React, { useState } from 'react';
import { COPY } from '@app/lib/content';
import { useLang } from '@app/lib/useLang';
import { QUESTS, QUEST_IDS, PRICE_EUR, mapUrl, pagePath, playUrl, type QuestId, type Lang } from '@app/lib/site';
import { track } from '@app/lib/track';
import { PageHero, QuestGallery, InteractiveMap } from '@widgets';
import { QUEST_MAPS } from '@app/lib/questMap';
import styles from '../pageStyles.module.css';

interface Props {
  quest: QuestId;
}

type Extra = { durationShort: string; players: string; includedTitle: string; included: string[] };
const EXTRA: Record<Lang, Extra> = {
  en: { durationShort: '~1.5 h', players: 'Players', includedTitle: "What's included", included: ['GPS navigation', 'Riddles', 'History', 'Scoring', 'Help', 'Three answer options'] },
  pt: { durationShort: '~1,5 h', players: 'Jogadores', includedTitle: 'O que está incluído', included: ['Navegação GPS', 'Enigmas', 'História', 'Pontuação', 'Ajuda', 'Três opções de resposta'] },
  ru: { durationShort: '~1,5 ч', players: 'Игроки', includedTitle: 'Что входит', included: ['GPS навигация', 'Загадки', 'История', 'Подсчёт баллов', 'Помощь', 'Три варианта ответа'] },
};

// Количество участников выбирается здесь, а не на странице Stripe: приложение передаёт его в оплату как есть.
const MIN_PEOPLE = 1;
const MAX_PEOPLE = 10;
const PEOPLE: Record<Lang, { label: string; less: string; more: string }> = {
  en: { label: 'Participants (children under 12 free)', less: 'One person less', more: 'One person more' },
  pt: { label: 'Participantes (crianças até 12 anos grátis)', less: 'Menos uma pessoa', more: 'Mais uma pessoa' },
  ru: { label: 'Участников (дети до 12 — бесплатно)', less: 'На одного меньше', more: 'На одного больше' },
};

const formatTotal = (people: number, lang: Lang) => {
  const total = (people * Number(PRICE_EUR)).toFixed(2);
  return `€${lang === 'en' ? total : total.replace('.', ',')}`;
};

const MAPTXT: Record<Lang, { title: string; hint: string }> = {
  en: { title: 'The route on the map', hint: 'Hover a photo to see it bigger — tap on mobile.' },
  pt: { title: 'O percurso no mapa', hint: 'Passe o rato por uma foto para a ver maior — toque no telemóvel.' },
  ru: { title: 'Маршрут на карте', hint: 'Наведите на фото, чтобы рассмотреть — на телефоне нажмите.' },
};

const PinIcon = () => (<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>);
const ClockIcon = () => (<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>);
const UsersIcon = () => (<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
const CheckIcon = () => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>);

const QuestPage: React.FC<Props> = ({ quest }) => {
  const lang = useLang();
  const c = COPY[lang];
  const q = c.quests[quest];
  const data = QUESTS[quest];
  const f = c.ui.facts;
  const e = EXTRA[lang];

  const facts: [string, React.ReactNode][] = [
    [f.format, f.formatValue],
    [f.price, f.priceValue],
    [f.duration, f.durationValue],
    [f.locations, f.locationsValue(data.locations)],
    [f.difficulty, q.difficulty],
    [f.age, data.age],
    [
      f.start,
      <a href={mapUrl(quest)} target="_blank" rel="noopener noreferrer">
        {q.startAddress}
      </a>,
    ],
    [f.languages, f.languagesValue],
    [f.terrain, f.terrainValue],
  ];

  const [people, setPeople] = useState(MIN_PEOPLE);
  const p = PEOPLE[lang];

  const buy = (place: string) => (
    <>
      <div className={styles.peoplePicker} role="group" aria-label={p.label}>
        <button
          type="button"
          className={styles.peopleBtn}
          onClick={() => setPeople((n) => Math.max(MIN_PEOPLE, n - 1))}
          disabled={people <= MIN_PEOPLE}
          aria-label={p.less}
        >
          −
        </button>
        <span className={styles.peopleCount} aria-live="polite">{people}</span>
        <button
          type="button"
          className={styles.peopleBtn}
          onClick={() => setPeople((n) => Math.min(MAX_PEOPLE, n + 1))}
          disabled={people >= MAX_PEOPLE}
          aria-label={p.more}
        >
          +
        </button>
      </div>
      <a
        className={styles.cta}
        href={`${playUrl(quest, lang)}&pay=1&qty=${people}`}
        onClick={() => track('route_cta_click', { route_id: quest, language: lang, cta_location: place, quantity: String(people) })}
      >
        {c.ui.buy} · {formatTotal(people, lang)}
      </a>
    </>
  );

  const haveAccess = (place: string) => (
    <a
      className={styles.ctaLink}
      href="https://play.algarve-quest.com/access"
      onClick={() => track('have_access_click', { route_id: quest, language: lang, cta_location: place })}
    >
      {c.ui.haveAccess}
    </a>
  );

  return (
    <main>
      <PageHero image={data.image} title={q.h1} lead={q.lead} />

      <div className={styles.wrapper}>
        <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
          <a href={pagePath('home', lang)}>{c.ui.home}</a> <span aria-hidden>›</span> <span>{q.city}</span>
        </nav>

        <div style={{ margin: '20px 0 4px' }}>
          <QuestGallery
            quest={quest}
            alt={q.h1}
            w={1200}
            h={900}
            rounded
            mapNode={QUEST_MAPS[quest] ? <InteractiveMap quest={quest} alt={q.h1} hint={MAPTXT[lang].hint} /> : undefined}
          />
        </div>

        <section className={styles.section}>
          {q.intro.map((p, i) => (
            <p key={i} className={styles.text}>
              {p}
            </p>
          ))}
          <p className={styles.peopleLabel}>{p.label}</p>
          <div className={styles.ctaRow}>
            {buy('route_intro')}
            {haveAccess('route_intro')}
          </div>
        </section>

        <section className={styles.statTiles} aria-label="Quest at a glance">
          <div className={styles.statTile}>
            <span className={styles.statIcon}><PinIcon /></span>
            <span className={styles.statValue}>{data.locations}</span>
            <span className={styles.statLabel}>{f.locations}</span>
          </div>
          <div className={styles.statTile}>
            <span className={styles.statIcon}><ClockIcon /></span>
            <span className={styles.statValue}>{e.durationShort}</span>
            <span className={styles.statLabel}>{f.duration}</span>
          </div>
          <div className={styles.statTile}>
            <span className={styles.statIcon}><UsersIcon /></span>
            <span className={styles.statValue}>1–10</span>
            <span className={styles.statLabel}>{e.players}</span>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>{c.ui.factsTitle}</h2>
          <dl className={styles.facts}>
            {facts.map(([label, value]) => (
              <div key={label} className={styles.factRow}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>{q.highlightsTitle}</h2>
          <ul className={styles.list}>
            {q.highlights.map((h) => (
              <li key={h}>{h}</li>
            ))}
          </ul>
          <p className={styles.note}>{q.paidNote}</p>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>{e.includedTitle}</h2>
          <ul className={styles.included}>
            {e.included.map((it) => (
              <li key={it} className={styles.includedItem}>
                <span className={styles.check}><CheckIcon /></span>
                {it}
              </li>
            ))}
          </ul>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>{c.ui.howTitle}</h2>
          <ol className={styles.steps}>
            {c.ui.howSteps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          <h3 className={styles.h3}>{c.ui.needTitle}</h3>
          <ul className={styles.list}>
            {c.ui.needItems.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
          <p className={styles.peopleLabel}>{p.label}</p>
          <div className={styles.ctaRow}>
            {buy('route_how')}
            {haveAccess('route_how')}
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>{c.ui.faqTitle}</h2>
          {c.ui.faq.map(({ q: question, a }) => (
            <details key={question} className={styles.faqItem}>
              <summary>{question}</summary>
              <p>{a}</p>
            </details>
          ))}
        </section>

        <section className={styles.section}>
          <h2 className={styles.h2}>{c.ui.otherRoutes}</h2>
          <ul className={styles.routeLinks}>
            {QUEST_IDS.filter((id) => id !== quest).map((id) => (
              <li key={id}>
                <a href={pagePath(id, lang)}>{c.quests[id].h1}</a>
              </li>
            ))}
          </ul>
          <p className={styles.text}>
            {c.ui.teamLink} <a href={pagePath('team', lang)}>{c.ui.teamLinkText}</a>
          </p>
        </section>
      </div>
    </main>
  );
};

export default QuestPage;
